import {
  EVENTS_FILTERED,
  EVENTS_ROUTE,
  EVENTS_ROUTE_NEW,
} from "../http-routes";
import { Event } from "../../models/event/event";
import { EventsPaginatedResponse } from "@api/models/event/events-paginated-response";
import { HttpService } from "../http.service";
import {
  DefaultResponse,
  IDefaultListResponse,
  IDefaultResponse,
  IDefaultResponseT,
} from "@api/models/default-response/response";
import { Injectable } from "@angular/core";
import { Lineup } from "@api/models/lineup/lineup";
import { IEventTabs } from "@api/models/event/event-tabs";
import { ITableData } from "@api/models/table/table";
import { TickerItem } from "@api/models/ticker/ticker-item";
import { IEventStatusResponse } from "@api/models/event/event-status-response";
import { SportTypeService } from "@api/services/sport-type/sport-type.service";
import { SportType } from "@lib/models/sport-type/sport-type";
import { EventStreamInfoDto } from "./dto/event-streaming-info.dto";
import { EntityType } from "@lib/models/entity/entity-type";

export enum EventsTargetType {
  Team ="team",
  League = "league",
  Season = "season"
}

export enum EventsOrderType {
  Future = "future",
  Past = "past",
  Today = "today",
  Upcoming = "upcoming"
}

interface IPutScoreResultData {
  regular_firsthalf_end?: string;
  regular_secondhalf_end?: string;
  overtime_firsthalf_end?: string;
  overtime_secondhalf_end?: string;
  shootout_end?: string;
}

export class GetEventsByDateRequest {
  filter: string;
  date: Date;
  skip = 0;
  limit = 0;
  sex?: string;
  entityId?: string;
  live?: boolean;
}

@Injectable({
  providedIn: "root",
})
export class EventsHttpService {
  constructor(protected http: HttpService) {}

  public registerAsEventReporter(
    eventId: string,
    teamId: string,
    token?: string,
    confirm?: boolean
  ): Promise<IDefaultResponse> {
    const body = { token, confirm };
    return this.http
      .put<IDefaultResponse>(
        [EVENTS_ROUTE_NEW, "reporter", "register", eventId, teamId],
        body
      )
      .toPromise();
  }

  public unregisterAsEventReporter(
    eventId: string,
    teamId: string
  ): Promise<IDefaultResponse> {
    return this.http
      .put<IDefaultResponse>([
        EVENTS_ROUTE_NEW,
        "reporter",
        "unregister",
        eventId,
        teamId,
      ])
      .toPromise();
  }

  public getStatus(eventId: string): Promise<IDefaultResponseT<IEventStatusResponse>> {
    return this.http.get([EVENTS_ROUTE_NEW, "status", eventId]);
  }

  public getLineup(
    eventId: string,
    teamId: string
  ): Promise<IDefaultResponseT<Lineup>> {
    return this.http.get<IDefaultResponseT<Lineup>>([
      EVENTS_ROUTE,
      eventId,
      "lineup",
      teamId,
    ]);
  }

  public getLiveLineup(
    eventId: string,
    teamId: string
  ): Promise<IDefaultResponseT<Lineup>> {
    return this.http.get<IDefaultResponseT<Lineup>>([
      EVENTS_ROUTE,
      eventId,
      "live_lineup",
      teamId,
    ]);
  }

  public getTimeline(eventId: string, teamId: string): Promise<TickerItem[]> {
    return this.http.get<any>([EVENTS_ROUTE, eventId, "timeline", teamId]);
  }

  public getFullTimeline(
    eventId: string,
    teamId: string
  ): Promise<TickerItem[]> {
    return this.http.get<any>([EVENTS_ROUTE, eventId, "fulltimeline", teamId]);
  }

  public getEventTabs(
    eventId: string,
    onlyVerified = false
  ): Promise<IDefaultResponseT<IEventTabs>> {
    const params = [];
    if (onlyVerified) {
      params.push("verified=true");
    }
    return this.http.get([EVENTS_ROUTE_NEW, eventId, "tabs"], params);
  }

  public getEventTable(
    type: "total" | "home" | "away",
    eventId: string
  ): Promise<IDefaultResponseT<ITableData[]>> {
    return this.http.get([EVENTS_ROUTE_NEW, "table", type, eventId]);
  }

  public async getLatestEventsForRegion(
    region: "national" | "international",
    limit = 6
  ): Promise<IDefaultListResponse<Event>> {
    return this.http.get<IDefaultListResponse<Event>>([
      EVENTS_ROUTE_NEW,
      "latest",
      region,
      limit,
    ]);
  }

  public async getSeasonEventsForRound(
    seasonId: string,
    round: number
  ): Promise<IDefaultListResponse<Event>> {
    return this.http.get<IDefaultListResponse<Event>>([
      EVENTS_ROUTE_NEW,
      "season",
      seasonId,
      "round",
      round,
    ]);
  }

  public async getCurrentLiveEvents(
    countryId: string,
    pageSize: number
  ): Promise<IDefaultListResponse<any>> {
    const urlParams = [
      "selection=live",
      `page_size=${pageSize}`,
      "current_page=0",
      "sex=male",
    ];
    return this.http.get<any>(["eventslist", "country", countryId], urlParams);
  }

  public async postEvent(event: {}): Promise<any> {
    return this.http.post<any>([EVENTS_ROUTE], event).toPromise();
  }

  public async getEvent(eventId: string, cache = true): Promise<Event> {
    let route = SportTypeService.selectedSportType === SportType.Football
        ? EVENTS_ROUTE
        : "handball/events";
    if (!cache) {
      route = SportTypeService.selectedSportType === SportType.Football
        ? "v2/events/by-id"
        : "handball/events/internal";
    }
    const response = await this.http.get<any>([route, eventId]);
    return response?.data ?? response;
  }

  public async getEventByLoalaId(
    loalaId: string
  ): Promise<IDefaultResponseT<Event>> {
    return this.http.get([EVENTS_ROUTE_NEW, "sportradar", "match-id", loalaId]);
  }

  public async putEvent(eventId: string, event: {}): Promise<Event> {
    return this.http.put<Event>([EVENTS_ROUTE, eventId], event).toPromise();
  }

  /**
   * Changes the score of a game that is active
   *
   * @param eventId string where to change score
   * @param data object with score field format "1:0"
   */
  public async putCurrentScore(
    eventId: string,
    data: { score: string; creator_teamid: string }
  ): Promise<Event> {
    return await this.http
      .put<Event>([EVENTS_ROUTE, eventId, "score"], data)
      .toPromise();
  }

  /**
   * Change final score - only usable in phases postgame or ended
   *
   * @param eventId string where to change score
   * @param score string with format "1:0"
   */
  public async putScoreAmendmend(
    eventId: string,
    score: { score: string }
  ): Promise<Event> {
    return await this.http
      .put<Event>([EVENTS_ROUTE, eventId, "scoreamendment"], score)
      .toPromise();
  }

  /**
   * Change score in particular phases.
   * Throws error if phase end is not reached
   *
   * @param eventId
   * @param data
   */
  public async putScoreResults(
    eventId: string,
    data: IPutScoreResultData
  ): Promise<Event> {
    return await this.http
      .put<Event>([EVENTS_ROUTE, eventId, "scoreresults"], data)
      .toPromise();
  }

  public async putEventSponsor(
    eventId: string,
    teamId: string,
    sponsorId: string
  ): Promise<any> {
    const event = {
      sponsor: {
        teamid: teamId,
        sponsorid: sponsorId,
      },
    };
    return this.http.put<any>([EVENTS_ROUTE, eventId], event);
  }

  public async patchEventSponsors(
    eventId: string,
    teamId: string,
    sponsorIds: string[] = []
  ): Promise<IDefaultResponse> {
    return this.http
      .patch<IDefaultResponse>(
        [EVENTS_ROUTE_NEW, "sponsors", eventId, "team", teamId],
        { sponsorids: sponsorIds }
      )
      .toPromise();
  }

  public async getEventsForTeamPaginated(
    teamId: string,
    order: EventsOrderType,
    skip = 0,
    limit = 0
  ): Promise<EventsPaginatedResponse> {
    return await this.getEventsForTargetPaginated(
      EventsTargetType.Team,
      teamId,
      order,
      skip,
      limit
    );
  }

  public async getEventsForLeaguePaginated(
    leagueId: string,
    order: EventsOrderType,
    skip = 0,
    limit = 0
  ): Promise<EventsPaginatedResponse> {
    return await this.getEventsForTargetPaginated(
      EventsTargetType.League,
      leagueId,
      order,
      skip,
      limit
    );
  }

  public async getEventsForSeasonPaginated(
    seasonId: string,
    order: EventsOrderType,
    skip = 0,
    limit = 0
  ): Promise<EventsPaginatedResponse> {
    return await this.getEventsForTargetPaginated(
      EventsTargetType.Season,
      seasonId,
      order,
      skip,
      limit
    );
  }

  public async getEventsForLeagueByRound(
    leagueId: string,
    round_name: string
  ): Promise<Event[]> {
    const response = await this.http.get<IDefaultListResponse<Event>>([
      EVENTS_ROUTE_NEW,
      "round-name",
      "league",
      leagueId,
      round_name,
    ]);
    return response.data;
  }

  public async setLineupShared(
    eventId: string,
    isHomeTeam: boolean
  ): Promise<Event> {
    if (isHomeTeam) {
      return this.putEvent(eventId, { lineup_shared_home: true });
    } else {
      return this.putEvent(eventId, { lineup_shared_away: true });
    }
  }

  public async getEventsForTarget(
    target: EventsTargetType,
    targetId: string,
    pastLimit = 0,
    futureLimit = 0
  ): Promise<Partial<Event>[]> {
    const response = await this.http.get<EventsPaginatedResponse>([
      EVENTS_ROUTE_NEW,
      target,
      targetId,
      pastLimit,
      futureLimit,
    ]);
    if (response.success) {
      return response.data;
    }
    return [];
  }

  public async getEventsForTargetPaginated(
    target: EventsTargetType,
    targetId: string,
    order: EventsOrderType,
    skip = 0,
    limit = 0
  ): Promise<EventsPaginatedResponse> {
    return await this.http.get<EventsPaginatedResponse>([
      EVENTS_ROUTE_NEW,
      target,
      targetId,
      order,
      skip,
      limit,
    ]);
  }

  private getDateForRequest(d: Date): string {
    return (
      ("0" + d.getDate()).slice(-2) +
      "-" +
      ("0" + (d.getMonth() + 1)).slice(-2) +
      "-" +
      d.getFullYear()
    );
  }

  public async getEventsByDate(options: GetEventsByDateRequest): Promise<EventsPaginatedResponse> {
    options = { ...new GetEventsByDateRequest(), ...options };
    const urlParams = [];
    if (options.sex != null) {
      urlParams.push(`sex=${options.sex}`);
    }
    if (options.filter === EntityType.State) {
      urlParams.push(`stateid=${options.entityId}`);
    }
    if (options.live != null) {
      urlParams.push(`live=${options.live}`);
    }
    if (options.filter === EntityType.Team) {
      urlParams.push(`teamid=${options.entityId}`);
    }
    if (options.filter === EntityType.League) {
      urlParams.push(`leagueid=${options.entityId}`);
    }
    return await this.http.get<EventsPaginatedResponse>(
      [EVENTS_FILTERED, options.filter, this.getDateForRequest(options.date), options.skip, options.limit],
      urlParams
    );
  }

  public async getEventsByScopeAndOrder(scope: string, order: EventsOrderType,
                                        skip = 0, limit = 0): Promise<IDefaultListResponse<Event>> {
    return this.http.get<any>(['handball/events/filter', scope, order, skip, limit]);
  }

  public async patchEventStreamingInfo(eventId: string, data: EventStreamInfoDto): Promise<IDefaultResponse> {
    let route = EVENTS_ROUTE_NEW;
    if (SportTypeService.selectedSportType === SportType.Handball) {
      route = `handball/events`;
    }
    return this.http.patch<IDefaultResponse>([route, 'streaming-info', eventId], data).toPromise();
  }

  public async getEventStreamDetails(eventId: string): Promise<any> {
    return this.http.get<DefaultResponse>([EVENTS_ROUTE_NEW, 'stream/infos', eventId]);
  }

  public async patchSetHlsVideo(eventId: string, type: 'replay' | 'highlights', hlsUrl: string): Promise<any> {
    const data = {
      vod_type: type,
      hls_url: hlsUrl,
    };
    return this.http.patch([EVENTS_ROUTE_NEW, 'hls', eventId], data).toPromise();
  }
}
